import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CookieConsentBanner = ({ onClose }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true,
    functional: true,
    analytics: false,
    marketing: false,
    personalization: false
  });

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
      personalization: true
    };
    localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
    localStorage.setItem('cookieConsentGiven', 'true');
    onClose?.();
  };

  const handleAcceptSelected = () => {
    localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences));
    localStorage.setItem('cookieConsentGiven', 'true');
    onClose?.();
  };

  const handleRejectOptional = () => {
    const onlyEssential = {
      essential: true,
      functional: false,
      analytics: false,
      marketing: false,
      personalization: false
    };
    localStorage.setItem('cookiePreferences', JSON.stringify(onlyEssential));
    localStorage.setItem('cookieConsentGiven', 'true');
    onClose?.();
  };

  const togglePreference = (key) => {
    if (key === 'essential') return; // Cannot disable essential cookies
    setCookiePreferences(prev => ({
      ...prev,
      [key]: !prev?.[key]
    }));
  };

  const cookieTypes = [
    {
      key: 'essential',
      name: 'Essentiels',
      description: 'Nécessaires au fonctionnement',
      required: true
    },
    {
      key: 'functional',
      name: 'Fonctionnels',
      description: 'Améliorent l\'expérience',
      required: false
    },
    {
      key: 'analytics',
      name: 'Analyse',
      description: 'Statistiques anonymisées',
      required: false
    },
    {
      key: 'marketing',
      name: 'Marketing',
      description: 'Publicités personnalisées',
      required: false
    },
    {
      key: 'personalization',
      name: 'Personnalisation',
      description: 'Contenu adapté',
      required: false
    }
  ];

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t shadow-lg">
      <div className="max-w-7xl mx-auto px-4 py-4">
        {!showDetails ? (
          /* Simple Banner */
          (<div className="flex flex-col md:flex-row items-start md:items-center justify-between space-y-4 md:space-y-0">
            <div className="flex items-start space-x-3 flex-1">
              <Icon name="Cookie" size={24} color="var(--color-brand-earth)" />
              <div>
                <h3 className="font-heading font-semibold text-brand-earth">
                  Nous utilisons des cookies
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Nous utilisons des cookies pour améliorer votre expérience sur KAPITALSTATES 
                  et vous proposer des services personnalisés.
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowDetails(true)}
                className="text-brand-earth hover:text-brand-gold text-sm font-medium transition-colors"
              >
                Personnaliser
              </button>
              
              <Button
                onClick={handleRejectOptional}
                variant="outline"
                size="sm"
              >
                Refuser
              </Button>
              
              <Button
                onClick={handleAcceptAll}
                variant="primary"
                size="sm"
                iconName="Check"
                iconPosition="left"
              >
                Tout Accepter
              </Button>
            </div>
          </div>)
        ) : (
          /* Detailed Banner */
          (<div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Icon name="Cookie" size={24} color="var(--color-brand-earth)" />
                <h3 className="font-heading font-semibold text-brand-earth">
                  Personnaliser vos Cookies
                </h3>
              </div>
              <button
                onClick={() => setShowDetails(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Icon name="ChevronUp" size={20} color="var(--color-gray-500)" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              {cookieTypes?.map((type) => (
                <div
                  key={type?.key}
                  className={`p-3 border rounded-lg transition-all ${
                    cookiePreferences?.[type?.key]
                      ? 'border-brand-gold bg-brand-gold/5' :'border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-body font-medium text-sm text-gray-800">
                      {type?.name}
                    </span>
                    <button
                      onClick={() => togglePreference(type?.key)}
                      disabled={type?.required}
                      className={`w-10 h-5 rounded-full transition-colors relative ${
                        cookiePreferences?.[type?.key] 
                          ? 'bg-brand-gold' :'bg-gray-300'
                      } ${type?.required ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <div className={`w-4 h-4 bg-white rounded-full shadow-sm transition-transform absolute top-0.5 ${
                        cookiePreferences?.[type?.key] 
                          ? 'translate-x-5' :'translate-x-0.5'
                      }`} />
                    </button>
                  </div>
                  <p className="text-xs text-gray-600">
                    {type?.description}
                  </p>
                  {type?.required && (
                    <span className="inline-block mt-1 text-xs text-red-600">
                      Requis
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between pt-2">
              <a
                href="/legal-compliance-footer"
                className="text-brand-earth hover:text-brand-gold text-sm font-medium transition-colors"
              >
                En savoir plus sur les cookies
              </a>
              
              <div className="flex items-center space-x-3">
                <Button
                  onClick={handleRejectOptional}
                  variant="outline"
                  size="sm"
                >
                  Refuser Optionnels
                </Button>
                
                <Button
                  onClick={handleAcceptSelected}
                  variant="secondary"
                  size="sm"
                >
                  Sauvegarder
                </Button>
                
                <Button
                  onClick={handleAcceptAll}
                  variant="primary"
                  size="sm"
                  iconName="Check"
                  iconPosition="left"
                >
                  Tout Accepter
                </Button>
              </div>
            </div>
          </div>)
        )}
      </div>
    </div>
  );
};

export default CookieConsentBanner;